<template>
  <div
    class="flex items-center p-2 shadow-sm gap-4 border rounded cursor-pointer"
    @click="onOpenFormClick(pageData)"
  >
    <!-- Title Section -->
    <div class="flex-grow">
      <div class="flex items-center">
        <!-- Icon -->
        <i
          class="pi text-gray pr-2 pl-1 py-3"
          :class="pageData.is_list ? `pi-list` : `pi-file`"
          style="font-size: 20px"
        ></i>

        <!-- Title -->
        <div class="font-medium text-gray-800">
          {{ pageData.label }}
        </div>
        <!-- Additional Info -->
        <div
          class="text-sm text-gray-500 px-2"
          v-if="
            Helper.isValueExist(pageData.min_required_items) && pageData.is_list
          "
        >
          {{ `(${pageData.list_items.length}/${pageData.min_required_items})` }}
        </div>
      </div>
    </div>

    <!-- KNob progress Section -->
    <div class="flex-shrink-0">
      <Knob
        v-model="progressIndicatorValue"
        :valueColor="progressIndicatorValue == 100 ? `LimeGreen` : `Orange`"
        valueTemplate="{value}%"
        :strokeWidth="8"
        :size="60"
        readonly
      />
    </div>
    <!-- Status Section -->
    <div class="flex-shrink-0">
      <Tag
        :severity="Helper.getQCProgressStatusColor(getCurrentStatus())"
        :value="getCurrentStatus()"
        class="text-xs md:text-sm"
      />
    </div>

    <!-- CRUD Options Section -->
    <div
      v-if="showCRUDOptions"
      class="flex-shrink-0 flex gap-1"
    >
      <Button
        severity="info"
        icon="pi pi-pencil"
        variant="text"
        @click.stop="onItemUpdateClick(pageData)"
      />
      <Button
        severity="danger"
        icon="pi pi-trash"
        variant="text"
        @click.stop="onDeleteItem"
      />
    </div>
  </div>
  <!-- for next page -->
  <FormPageDialogComponent
    v-if="localPageData.pageObj"
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :page="localPageData"
    :selectedPath="[...selectedPath, pageData.id]"
    :selectedLabels="[
      ...selectedLabels,
      { label: pageData.label, id: pageData.id }
    ]"
    @update:page="onUpdatedPageData"
    @update:navigateToBreadcrumb="navigateToPageInTheBreadcrumb"
    :title="localPageData.label"
    :isFullScreen="true"
    :savedResValue="savedResValue"
  />
  <!-- for parent to update same page -->
  <FormPageDialogComponent
    v-if="parentPage.parentPageData"
    :visible="parentPage.isVisible"
    @update:visible="parentPage.isVisible = $event"
    :page="parentPage.parentPageData"
    :selectedPath="selectedPath"
    :selectedLabels="selectedLabels"
    :title="parentPage.title"
    :isFullScreen="false"
    :onSaveClick="parentPage.onSaveClick"
    :savedResValue="savedResValue"
    :ignoreOverridePageValue="true"
    :itemId="parentPage.itemId"
    :updateLocalStore="false"
    />
  <FullScreenOverlayLoader
    :isLoading="loaderObj.isLoading"
    :message="loaderObj.loaderMsg"
  />
  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
</template>

<script>
  import { defineComponent, ref, computed } from "vue";
  import Helper from "@/utils/helper";
  import Constants from "@/utils/constants";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import FormPageDialogComponent from "./FormPageDialogComponent.vue";
  import { useFormPageStore } from "@/stores/formPageStore";
  import { useFormTemplateResponseStore } from "@/stores/formTemplateResponseStore";
  import { storeToRefs } from "pinia";
  import { useRoute } from "vue-router";

  export default defineComponent({
    name: "ProceduresTemplateComponent",
    components: {
      FullScreenOverlayLoader,
      ErrorDialog,
      FormPageDialogComponent
    },
    props: {
      status: {
        type: String,
        required: true
      },
      pageData: {
        type: Object,
        required: true
      },
      parentPageData: {
        type: Object
      },
      resId: {
        type: String
      },
      showCRUDOptions: {
        type: Boolean,
        default: false
      },
      onDeleteItem: {
        type: Function
      },
      selectedPath: {
        type: Array,
        default: () => []
      },
      savedResValue: {
        type: Object,
        default: () => ({})
      },
      selectedLabels: {
        type: Array,
        default: () => []
      }
    },
    setup(props, { emit }) {
      const localPageData = ref(props.pageData);

      const route = useRoute();
      const formTemplateResponseStore = useFormTemplateResponseStore();
      const { response } = storeToRefs(formTemplateResponseStore);
      const progressIndicatorValue = computed(() => {
        const store = useFormPageStore();

        if (props.pageData.is_list) {
          // Collect all item IDs to calculate progress
          const itemIds = props.pageData.list_items.map((item) => item.id);
          return store.getFormProgress(props.pageData.id, itemIds);
        } else {
          return Math.round(store.getFormProgress(props.pageData.id));
        }
      });
      // only need to process existing list_items if it's list, on page view no need of that
      if (props.pageData.is_list) {
        // read the response obj for list_items if exits
        const _currentPageData = Helper.findAndSetKeyInObject(
          Helper.deepCopy(response.value),
          localPageData.value.id
        );
        // set response list items to local page data so that ui can have reflected data
        if (_currentPageData?.list_items) {
          localPageData.value.list_items = [
            ...localPageData.value.list_items,
            ..._currentPageData.list_items
          ];
        }
      }

      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });
      const loaderObj = ref({
        isLoading: false,
        loaderMsg: ""
      });
      const dialogVisible = ref(false);
      const parentPage = ref({
        isVisible: false,
        parentPageData: {},
        itemId: null,
        title: null,
        onSaveClick: (data) => {
          // reset the item id after save
          parentPage.value.itemId = null;
          emit("update:parentPageData", data);
        }
      });

      const navigateToPageInTheBreadcrumb = async (targetPageId) => {
        // current page id
        const _currentPageId = localPageData.value.id;
        // if target vs current page id is not same then close current dialog
        if (targetPageId !== _currentPageId) {
          dialogVisible.value = false;
          // wait for 100 ms
          await new Promise((resolve) => setTimeout(resolve, 100));
          emit("update:navigateToBreadcrumb", targetPageId);
        }
      };

      const onUpdatedPageData = async (data) => {
        localPageData.value = data;
        console.log("UPDATING STORE DATA AT PROCED....", data.id);
        await useFormPageStore().updateFormPageFromList(localPageData.value);
        emit("update:pageData", data);
      };

      const onOpenFormClick = async (data) => {
        console.log("=== ProceduresTemplateComponent Debug ===", data);

        console.log("new selectedPath:", [
          ...props.selectedPath,
          props.pageData.id
        ]);
        console.log("selectedLabels:", [
          ...props.selectedLabels,
          { label: props.pageData.label, id: props.pageData.id }
        ]);

        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Processing...";
          if (!localPageData.value.page_id)
            throw `No page found for ${Helper.getBoldTxt(data.label)}`;
          const { pageObj, ...restData } = data;

          localPageData.value = {
            ...restData
          };
          // // checking if it's in pinia store
          const _exisitingPageRes =
            await useFormPageStore().getFormPageFromList(
              localPageData.value.id
            );
          if (_exisitingPageRes) localPageData.value = _exisitingPageRes;
          // if form doesn't exists in local then fetch else use the old one.
          if (!localPageData.value.pageObj) {
            const _pageRes = await useFormPageStore().getFormPageById(
              localPageData.value.page_id,
              true
            );
            if (_pageRes.error) throw _pageRes.error;
            if (_pageRes.data) localPageData.value.pageObj = _pageRes.data;
            // props.showCRUDOptions = false , mean if no need to show then no need to update local progressdata and affect progress indicator
            let updatedData = await useFormPageStore().updateFormPageFromList(
              localPageData.value,
              !(props.showCRUDOptions == false),
              route.params
            );
            if (updatedData) {
              localPageData.value = updatedData;
            }
          }
          emit("update:pageData", localPageData.value);
          dialogVisible.value = true;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
        }
      };

      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };
      // function for handling edit
      const onItemUpdateClick = async (data) => {
        try {
          parentPage.value.title = `Update Form`;
          if (data.id) parentPage.value.itemId = data.id;
          let setInitialData =
            Object.keys(parentPage.value.parentPageData).length === 0;
          if (props.parentPageData && setInitialData) {
            parentPage.value.parentPageData = {
              pageObj: Helper.deepCopy(props.parentPageData)
            };
          }
          // if it's update data
          if (data) {
            const processedFields = Helper.processFieldValues(
              Helper.deepCopy(parentPage.value.parentPageData.pageObj.fields),
              data
            );
            parentPage.value.parentPageData.pageObj.fields = JSON.parse(
              JSON.stringify(processedFields)
            );
          }
          parentPage.value.isVisible = true;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      const getCurrentStatus = () => {
        if (progressIndicatorValue.value == 100) {
          return Constants.enums.PROGRESS_STATUS.COMPLETE;
        } else if (progressIndicatorValue.value == 0) {
          return Constants.enums.PROGRESS_STATUS.NOT_STARTED;
        } else {
          return Constants.enums.PROGRESS_STATUS.IN_PROGRESS;
        }
      };

      return {
        localPageData,
        progressIndicatorValue,
        Helper,
        Constants,
        loaderObj,
        errorObj,
        dialogVisible,
        parentPage,
        onOpenFormClick,
        onErrorDialogClose,
        onItemUpdateClick,
        getCurrentStatus,
        onUpdatedPageData,
        navigateToPageInTheBreadcrumb
      };
    }
  });
</script>
